import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { usePageSEO } from '../../hooks/usePageSEO';
import Breadcrumb from '../../components/feature/Breadcrumb';
import { createArticleStructuredData, createBreadcrumbStructuredData, createFAQStructuredData, createItemListStructuredData } from '../../utils/structuredData';

export default function HowToChoose() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://sho-navi.com';

  // SEO設定
  usePageSEO({
    title: 'FX初心者が失敗しない証券会社の選び方｜7つのチェックポイント',
    description: 'FX業者選びで失敗しないための7つのチェックポイントを解説。スプレッド、ツールの使いやすさ、サポート体制、安全性など、初心者が本当に重視すべき項目を分かりやすく説明します。DMM FXとの比較も掲載。',
    keywords: 'FX, 証券会社, 選び方, 比較, 初心者, おすすめ, チェックポイント',
    ogImage: 'https://public.readdy.ai/ai/img_res/45d29a350100d2858adf959eff295f8f.jpg',
    structuredData: [
      createArticleStructuredData({
        headline: 'FX初心者が失敗しない証券会社の選び方',
        description: 'FX業者選びの7つのチェックポイントを詳しく解説。スプレッド、取引ツール、最低取引単位、サポート体制、安全性、通貨ペア、手数料を比較し、初心者に最適な業者を紹介します。',
        image: 'https://readdy.ai/api/search-image?query=FX%20broker%20selection&width=1200&height=630&seq=choose-article&orientation=landscape',
        author: 'DMM FX情報局',
        datePublished: '2024-01-15',
        dateModified: new Date().toISOString().split('T')[0],
        url: `${siteUrl}/how-to-choose`
      }),
      // 7つのチェックポイントをItemList構造化データで表現
      createItemListStructuredData({
        name: 'FX業者選びの7つのチェックポイント',
        description: '初心者が失敗しないFX業者選びのための重要なチェックポイント一覧',
        items: [
          {
            name: 'スプレッドの狭さ',
            description: '取引コストに直結する重要な要素。米ドル/円0.2銭以下が目安。',
            url: `${siteUrl}/how-to-choose#point1`
          },
          {
            name: '取引ツールの使いやすさ',
            description: '初心者でも直感的に操作できるツールが必須。デモ口座で確認すべき。',
            url: `${siteUrl}/how-to-choose#point2`
          },
          {
            name: '最低取引単位',
            description: '少額から始められるか確認。1,000通貨以下が理想的。',
            url: `${siteUrl}/how-to-choose#point3`
          },
          {
            name: 'サポート体制',
            description: '24時間対応、複数の問い合わせ方法があるか確認。',
            url: `${siteUrl}/how-to-choose#point4`
          },
          {
            name: '安全性・信頼性',
            description: '金融庁登録、信託保全の有無、運営会社の実績を確認。',
            url: `${siteUrl}/how-to-choose#point5`
          },
          {
            name: '通貨ペアの種類',
            description: '主要通貨ペアが揃っているか。初心者は20種類程度で十分。',
            url: `${siteUrl}/how-to-choose#point6`
          },
          {
            name: '各種手数料',
            description: '取引手数料、入出金手数料、口座維持費が無料か確認。',
            url: `${siteUrl}/how-to-choose#point7`
          }
        ]
      }),
      // FAQ構造化データ
      createFAQStructuredData([
        {
          question: 'FX初心者はどのような基準で業者を選べばいいですか?',
          answer: 'スプレッドの狭さ、取引ツールの使いやすさ、サポート体制、安全性の4つを最優先に確認しましょう。特に初心者は24時間サポートがある業者を選ぶと安心です。DMM FXはこれらの条件を全て満たしています。'
        },
        {
          question: 'スプレッドが狭い業者を選ぶべき理由は何ですか?',
          answer: 'スプレッドは取引のたびに発生するコストです。特にデイトレードなど取引回数が多い場合、スプレッドの差が利益に大きく影響します。米ドル/円で0.2銭以下の業者を選ぶことをおすすめします。'
        },
        {
          question: '最低取引単位が大きい業者は避けるべきですか?',
          answer: '必ずしもそうではありません。DMM FXは10,000通貨からですが、デモ口座で無料練習ができ、レバレッジを活用すれば約6万円から取引可能です。業界最狭水準のスプレッドや充実したサポート体制など、他のメリットが大きければ問題ありません。'
        },
        {
          question: 'FX業者の安全性はどうやって確認すればいいですか?',
          answer: '金融庁に登録されているか、信託保全が完備されているか、運営会社の資本力や実績を確認しましょう。DMM FXは金融庁登録済み、全額信託保全完備、DMMグループの信頼性があり、安全性は非常に高いです。'
        },
        {
          question: 'DMM FXは初心者におすすめですか?',
          answer: 'はい、DMM FXは初心者に最もおすすめのFX業者です。業界最狭水準のスプレッド、使いやすいツール、24時間サポート、全額信託保全など、初心者が重視すべきポイントを全て満たしています。'
        }
      ], `${siteUrl}/how-to-choose`),
      createBreadcrumbStructuredData([
        { name: 'ホーム', url: '/' },
        { name: 'FX会社の選び方', url: '/how-to-choose' }
      ], siteUrl)
    ]
  });

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* パンくずリスト */}
      <div className="bg-gray-50 border-b border-gray-100">
        <div className="max-w-6xl mx-auto px-4 py-3">
          <Breadcrumb items={[
            { name: 'ホーム', url: '/' },
            { name: 'FX基礎知識', url: '/fx-basic' },
            { name: 'FX会社の選び方', url: '/how-to-choose' }
          ]} />
        </div>
      </div>
      
      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-gradient-to-br from-teal-50 via-emerald-50 to-white">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-12">
            <div className="inline-flex items-center bg-white border-2 border-red-500 rounded-full px-6 py-2 mb-6">
              <i className="ri-shield-check-line text-red-500 mr-2"></i>
              <span className="text-red-500 font-bold text-sm">失敗しないFX業者選びの決定版</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              FX初心者が失敗しない<br />証券会社の選び方
            </h1>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              FX業者選びで失敗しないための7つのチェックポイントを解説。初心者が本当に重視すべき項目を分かりやすく説明します。
            </p>
          </div>

          <div className="bg-white rounded-2xl shadow-lg p-8 max-w-4xl mx-auto">
            <div className="flex items-start">
              <i className="ri-lightbulb-line text-3xl text-teal-600 mr-4 mt-1"></i>
              <div>
                <h2 className="text-xl font-bold text-gray-900 mb-2">FX業者選びが重要な理由</h2>
                <p className="text-gray-700 leading-relaxed">
                  FX業者によって、取引コスト・ツールの使いやすさ・サポート体制が大きく異なります。自分に合った業者を選ぶことで、快適に取引でき、成功確率も高まります。この記事では、初心者が本当にチェックすべきポイントを厳選して解説します。
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 7つのチェックポイント */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">7つのチェックポイント</h2>
          
          <div className="space-y-8">
            {/* ポイント1 */}
            <div className="bg-gradient-to-br from-teal-50 to-emerald-50 rounded-2xl p-8">
              <div className="flex items-start mb-6">
                <div className="w-14 h-14 bg-teal-500 rounded-xl flex items-center justify-center mr-4 flex-shrink-0">
                  <span className="text-2xl font-bold text-white">1</span>
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">スプレッドの狭さ</h3>
                  <p className="text-gray-700 mb-4 leading-relaxed">
                    スプレッドは取引のたびに発生するコストです。特にデイトレードなど取引回数が多い場合、スプレッドの差が利益に大きく影響します。
                  </p>
                  <div className="bg-white rounded-xl p-6">
                    <h4 className="font-bold text-gray-900 mb-3">チェック方法</h4>
                    <ul className="space-y-2">
                      <li className="flex items-start text-sm text-gray-700">
                        <i className="ri-check-line text-teal-600 mr-2 mt-1"></i>
                        <span>米ドル/円のスプレッドが<span className="text-red-600 font-bold text-base">0.2銭以下</span>が目安</span>
                      </li>
                      <li className="flex items-start text-sm text-gray-700">
                        <i className="ri-check-line text-teal-600 mr-2 mt-1"></i>
                        <span>原則固定スプレッドかどうか確認</span>
                      </li>
                      <li className="flex items-start text-sm text-gray-700">
                        <i className="ri-check-line text-teal-600 mr-2 mt-1"></i>
                        <span>早朝や指標発表時のスプレッド拡大に注意</span>
                      </li>
                    </ul>
                    <div className="mt-4 p-5 bg-gradient-to-r from-red-50 to-orange-50 rounded-xl border-2 border-red-200">
                      <p className="text-gray-700">
                        <span className="text-red-600 font-black text-lg">DMM FXなら</span>
                        <span className="text-red-600 font-bold text-xl ml-2">米ドル/円0.2銭</span>
                        <span className="text-gray-700 text-base">（原則固定）、</span>
                        <span className="text-red-600 font-bold text-xl">ユーロ/円0.4銭</span>
                        <span className="text-gray-700 text-base">など</span>
                        <span className="bg-red-600 text-white px-2 py-1 rounded text-sm font-bold ml-2">業界最狭水準</span>
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* ポイント2 */}
            <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-2xl p-8">
              <div className="flex items-start mb-6">
                <div className="w-14 h-14 bg-emerald-500 rounded-xl flex items-center justify-center mr-4 flex-shrink-0">
                  <span className="text-2xl font-bold text-white">2</span>
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">取引ツールの使いやすさ</h3>
                  <p className="text-gray-700 mb-4 leading-relaxed">
                    初心者にとって、直感的に操作できるツールは必須です。複雑すぎるツールは、誤発注のリスクも高まります。
                  </p>
                  <div className="bg-white rounded-xl p-6">
                    <h4 className="font-bold text-gray-900 mb-3">チェック方法</h4>
                    <ul className="space-y-2">
                      <li className="flex items-start text-sm text-gray-700">
                        <i className="ri-check-line text-emerald-600 mr-2 mt-1"></i>
                        <span>デモ口座で実際に操作してみる</span>
                      </li>
                      <li className="flex items-start text-sm text-gray-700">
                        <i className="ri-check-line text-emerald-600 mr-2 mt-1"></i>
                        <span>スマホアプリの使いやすさも確認</span>
                      </li>
                      <li className="flex items-start text-sm text-gray-700">
                        <i className="ri-check-line text-emerald-600 mr-2 mt-1"></i>
                        <span>チャート機能が充実しているか</span>
                      </li>
                    </ul>
                    <div className="mt-4 p-5 bg-gradient-to-r from-red-50 to-orange-50 rounded-xl border-2 border-red-200">
                      <p className="text-gray-700">
                        <span className="text-red-600 font-black text-lg">DMM FXなら</span>
                        <span className="text-gray-700 text-base ml-2">初心者向けの</span>
                        <span className="text-red-600 font-bold text-lg">「DMMFX STANDARD」</span>
                        <span className="text-gray-700 text-base">と上級者向けの</span>
                        <span className="text-red-600 font-bold text-lg">「DMMFX PLUS」</span>
                        <span className="text-gray-700 text-base">を用意。</span>
                        <span className="bg-red-600 text-white px-2 py-1 rounded text-sm font-bold ml-2">レベルに合わせて選べる</span>
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* ポイント3 */}
            <div className="bg-gradient-to-br from-teal-50 to-emerald-50 rounded-2xl p-8">
              <div className="flex items-start mb-6">
                <div className="w-14 h-14 bg-teal-500 rounded-xl flex items-center justify-center mr-4 flex-shrink-0">
                  <span className="text-2xl font-bold text-white">3</span>
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">最低取引単位</h3>
                  <p className="text-gray-700 mb-4 leading-relaxed">
                    初心者は少額から始めたいもの。最低取引単位が小さいほど、リスクを抑えて練習できます。
                  </p>
                  <div className="bg-white rounded-xl p-6">
                    <h4 className="font-bold text-gray-900 mb-3">チェック方法</h4>
                    <ul className="space-y-2">
                      <li className="flex items-start text-sm text-gray-700">
                        <i className="ri-check-line text-teal-600 mr-2 mt-1"></i>
                        <span>1,000通貨から取引できるか確認</span>
                      </li>
                      <li className="flex items-start text-sm text-gray-700">
                        <i className="ri-check-line text-teal-600 mr-2 mt-1"></i>
                        <span>必要証拠金がいくらか計算する</span>
                      </li>
                      <li className="flex items-start text-sm text-gray-700">
                        <i className="ri-check-line text-teal-600 mr-2 mt-1"></i>
                        <span>自分の資金に合った単位を選ぶ</span>
                      </li>
                    </ul>
                    <div className="mt-4 p-5 bg-gradient-to-r from-red-50 to-orange-50 rounded-xl border-2 border-red-200">
                      <p className="text-gray-700">
                        <span className="text-red-600 font-black text-lg">DMM FXなら</span>
                        <span className="text-red-600 font-bold text-xl ml-2">10,000通貨（1Lot）</span>
                        <span className="text-gray-700 text-base">から。レバレッジ25倍で</span>
                        <span className="text-red-600 font-bold text-xl">約6万円</span>
                        <span className="text-gray-700 text-base">から取引可能</span>
                        <span className="bg-red-600 text-white px-2 py-1 rounded text-sm font-bold ml-2">※1ドル=150円の場合</span>
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* ポイント4 */}
            <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-2xl p-8">
              <div className="flex items-start mb-6">
                <div className="w-14 h-14 bg-emerald-500 rounded-xl flex items-center justify-center mr-4 flex-shrink-0">
                  <span className="text-2xl font-bold text-white">4</span>
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">サポート体制</h3>
                  <p className="text-gray-700 mb-4 leading-relaxed">
                    初心者は分からないことが多いもの。困ったときにすぐ相談できるサポート体制は重要です。
                  </p>
                  <div className="bg-white rounded-xl p-6">
                    <h4 className="font-bold text-gray-900 mb-3">チェック方法</h4>
                    <ul className="space-y-2">
                      <li className="flex items-start text-sm text-gray-700">
                        <i className="ri-check-line text-emerald-600 mr-2 mt-1"></i>
                        <span>電話サポートの対応時間を確認</span>
                      </li>
                      <li className="flex items-start text-sm text-gray-700">
                        <i className="ri-check-line text-emerald-600 mr-2 mt-1"></i>
                        <span>メール・チャット・LINEなど問い合わせ方法</span>
                      </li>
                      <li className="flex items-start text-sm text-gray-700">
                        <i className="ri-check-line text-emerald-600 mr-2 mt-1"></i>
                        <span>FAQやマニュアルが充実しているか</span>
                      </li>
                    </ul>
                    <div className="mt-4 p-5 bg-gradient-to-r from-red-50 to-orange-50 rounded-xl border-2 border-red-200">
                      <p className="text-gray-700">
                        <span className="text-red-600 font-black text-lg">DMM FXなら</span>
                        <span className="text-red-600 font-bold text-xl ml-2">24時間サポート</span>
                        <span className="text-gray-700 text-base">（LINE/電話/メール対応）。</span>
                        <span className="bg-red-600 text-white px-2 py-1 rounded text-sm font-bold ml-2">HDI格付け3年連続三つ星</span>
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* 利用者の声へのリンク */}
              <div className="mt-6 bg-white rounded-xl p-6 border-l-4 border-teal-500 shadow-md">
                <p className="text-gray-700 leading-relaxed">
                  実際の利用者の声や評判については、こちらの記事で詳しくまとめています。<br />
                  → <Link to="/reviews" className="text-teal-600 font-semibold hover:text-teal-700 underline cursor-pointer">「DMM FXの口コミ・評判は？実際どうなのか調査」</Link>
                </p>
              </div>
            </div>

            {/* ポイント5 */}
            <div className="bg-gradient-to-br from-teal-50 to-emerald-50 rounded-2xl p-8">
              <div className="flex items-start mb-6">
                <div className="w-14 h-14 bg-teal-500 rounded-xl flex items-center justify-center mr-4 flex-shrink-0">
                  <span className="text-2xl font-bold text-white">5</span>
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">安全性・信頼性</h3>
                  <p className="text-gray-700 mb-4 leading-relaxed">
                    大切な資産を預けるのですから、業者の安全性は最重要項目です。
                  </p>
                  <div className="bg-white rounded-xl p-6">
                    <h4 className="font-bold text-gray-900 mb-3">チェック方法</h4>
                    <ul className="space-y-2">
                      <li className="flex items-start text-sm text-gray-700">
                        <i className="ri-check-line text-teal-600 mr-2 mt-1"></i>
                        <span>金融庁に登録された業者か確認</span>
                      </li>
                      <li className="flex items-start text-sm text-gray-700">
                        <i className="ri-check-line text-teal-600 mr-2 mt-1"></i>
                        <span>信託保全が完備されているか</span>
                      </li>
                      <li className="flex items-start text-sm text-gray-700">
                        <i className="ri-check-line text-teal-600 mr-2 mt-1"></i>
                        <span>運営会社の資本力や実績を確認</span>
                      </li>
                    </ul>
                    <div className="mt-4 p-5 bg-gradient-to-r from-red-50 to-orange-50 rounded-xl border-2 border-red-200">
                      <p className="text-gray-700">
                        <span className="text-red-600 font-black text-lg">DMM FXなら</span>
                        <span className="text-red-600 font-bold text-lg ml-2">金融庁登録済み</span>
                        <span className="text-gray-700 text-base">・</span>
                        <span className="text-red-600 font-bold text-lg">全額信託保全</span>
                        <span className="text-gray-700 text-base">・</span>
                        <span className="bg-red-600 text-white px-2 py-1 rounded text-sm font-bold ml-2">DMMグループの信頼性</span>
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* ポイント6 */}
            <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-2xl p-8">
              <div className="flex items-start mb-6">
                <div className="w-14 h-14 bg-emerald-500 rounded-xl flex items-center justify-center mr-4 flex-shrink-0">
                  <span className="text-2xl font-bold text-white">6</span>
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">通貨ペアの種類</h3>
                  <p className="text-gray-700 mb-4 leading-relaxed">
                    初心者は主要通貨ペアで十分ですが、将来的に選択肢が広がるのは良いことです。
                  </p>
                  <div className="bg-white rounded-xl p-6">
                    <h4 className="font-bold text-gray-900 mb-3">チェック方法</h4>
                    <ul className="space-y-2">
                      <li className="flex items-start text-sm text-gray-700">
                        <i className="ri-check-line text-emerald-600 mr-2 mt-1"></i>
                        <span>米ドル/円、ユーロ/円など主要通貨ペアがあるか</span>
                      </li>
                      <li className="flex items-start text-sm text-gray-700">
                        <i className="ri-check-line text-emerald-600 mr-2 mt-1"></i>
                        <span>取引したい通貨ペアが含まれているか</span>
                      </li>
                      <li className="flex items-start text-sm text-gray-700">
                        <i className="ri-check-line text-emerald-600 mr-2 mt-1"></i>
                        <span>初心者は20通貨ペア程度あれば十分</span>
                      </li>
                    </ul>
                    <div className="mt-4 p-5 bg-gradient-to-r from-red-50 to-orange-50 rounded-xl border-2 border-red-200">
                      <p className="text-gray-700">
                        <span className="text-red-600 font-black text-lg">DMM FXなら</span>
                        <span className="text-red-600 font-bold text-xl ml-2">21通貨ペア</span>
                        <span className="text-gray-700 text-base">を取り扱い。</span>
                        <span className="bg-red-600 text-white px-2 py-1 rounded text-sm font-bold ml-2">主要通貨ペアは全て網羅</span>
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* ポイント7 */}
            <div className="bg-gradient-to-br from-teal-50 to-emerald-50 rounded-2xl p-8">
              <div className="flex items-start mb-6">
                <div className="w-14 h-14 bg-teal-500 rounded-xl flex items-center justify-center mr-4 flex-shrink-0">
                  <span className="text-2xl font-bold text-white">7</span>
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">各種手数料</h3>
                  <p className="text-gray-700 mb-4 leading-relaxed">
                    スプレッド以外にも、入出金手数料や口座維持費など、隠れたコストがないか確認しましょう。
                  </p>
                  <div className="bg-white rounded-xl p-6">
                    <h4 className="font-bold text-gray-900 mb-3">チェック方法</h4>
                    <ul className="space-y-2">
                      <li className="flex items-start text-sm text-gray-700">
                        <i className="ri-check-line text-teal-600 mr-2 mt-1"></i>
                        <span>取引手数料が無料か確認</span>
                      </li>
                      <li className="flex items-start text-sm text-gray-700">
                        <i className="ri-check-line text-teal-600 mr-2 mt-1"></i>
                        <span>入金・出金手数料をチェック</span>
                      </li>
                      <li className="flex items-start text-sm text-gray-700">
                        <i className="ri-check-line text-teal-600 mr-2 mt-1"></i>
                        <span>口座維持費が発生しないか確認</span>
                      </li>
                    </ul>
                    <div className="mt-4 p-5 bg-gradient-to-r from-red-50 to-orange-50 rounded-xl border-2 border-red-200">
                      <p className="text-gray-700">
                        <span className="text-red-600 font-black text-lg">DMM FXなら</span>
                        <span className="text-red-600 font-bold text-xl ml-2">各種手数料0円</span>
                        <span className="text-gray-700 text-base">（取引手数料・口座維持費・クイック入金・出金手数料すべて</span>
                        <span className="bg-red-600 text-white px-2 py-1 rounded text-sm font-bold ml-2">完全無料</span>
                        <span className="text-gray-700 text-base">）</span>
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* DMM FXまとめセクション */}
      <section className="py-16 bg-gradient-to-br from-red-50 to-orange-50">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              <span className="text-red-600">DMM FX</span>なら7つのポイントを<span className="bg-red-600 text-white px-3 py-1 rounded text-3xl ml-2">すべてクリア</span>
            </h2>
            <p className="text-gray-600">初心者が重視すべきポイントを全て満たしています</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-white rounded-xl p-6 shadow-lg border-t-4 border-red-500">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mb-4">
                <i className="ri-money-yen-circle-line text-red-600 text-2xl"></i>
              </div>
              <h3 className="font-bold text-gray-900 mb-2">スプレッド</h3>
              <p className="text-red-600 font-bold text-2xl">0.2銭</p>
              <p className="text-sm text-gray-500">米ドル/円（原則固定）</p>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-lg border-t-4 border-red-500">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mb-4">
                <i className="ri-customer-service-2-line text-red-600 text-2xl"></i>
              </div>
              <h3 className="font-bold text-gray-900 mb-2">サポート</h3>
              <p className="text-red-600 font-bold text-2xl">24時間</p>
              <p className="text-sm text-gray-500">LINE/電話/メール対応</p>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-lg border-t-4 border-red-500">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mb-4">
                <i className="ri-hand-coin-line text-red-600 text-2xl"></i>
              </div>
              <h3 className="font-bold text-gray-900 mb-2">各種手数料</h3>
              <p className="text-red-600 font-bold text-2xl">0円</p>
              <p className="text-sm text-gray-500">入出金・取引手数料無料</p>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-lg border-t-4 border-red-500">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mb-4">
                <i className="ri-shield-check-line text-red-600 text-2xl"></i>
              </div>
              <h3 className="font-bold text-gray-900 mb-2">安全性</h3>
              <p className="text-red-600 font-bold text-lg">全額信託保全</p>
              <p className="text-sm text-gray-500">金融庁登録済み</p>
            </div>
          </div>
        </div>
      </section>

      {/* 比較表 */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">主要FX業者の比較</h2>
          
          <div className="overflow-x-auto">
            <table className="w-full bg-white rounded-xl shadow-lg overflow-hidden">
              <thead className="bg-gradient-to-r from-teal-500 to-emerald-600 text-white">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-bold">項目</th>
                  <th className="px-6 py-4 text-center text-sm font-bold bg-red-500">DMM FX</th>
                  <th className="px-6 py-4 text-center text-sm font-bold">GMOクリック証券</th>
                  <th className="px-6 py-4 text-center text-sm font-bold">外為どっとコム</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                <tr className="hover:bg-gray-50">
                  <td className="px-6 py-4 text-sm font-medium text-gray-900">米ドル/円スプレッド</td>
                  <td className="px-6 py-4 text-sm text-center bg-red-50">
                    <span className="text-red-600 font-bold text-lg">0.2銭</span>
                  </td>
                  <td className="px-6 py-4 text-sm text-center text-gray-700">0.2銭</td>
                  <td className="px-6 py-4 text-sm text-center text-gray-700">0.2銭</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-6 py-4 text-sm font-medium text-gray-900">最低取引単位</td>
                  <td className="px-6 py-4 text-sm text-center bg-red-50">
                    <span className="text-red-600 font-bold">10,000通貨</span>
                  </td>
                  <td className="px-6 py-4 text-sm text-center text-gray-700">1,000通貨</td>
                  <td className="px-6 py-4 text-sm text-center text-gray-700">1,000通貨</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-6 py-4 text-sm font-medium text-gray-900">通貨ペア数</td>
                  <td className="px-6 py-4 text-sm text-center bg-red-50">
                    <span className="text-red-600 font-bold">21種類</span>
                  </td>
                  <td className="px-6 py-4 text-sm text-center text-gray-700">20種類</td>
                  <td className="px-6 py-4 text-sm text-center text-gray-700">30種類</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-6 py-4 text-sm font-medium text-gray-900">サポート時間</td>
                  <td className="px-6 py-4 text-sm text-center bg-red-50">
                    <span className="text-red-600 font-bold text-lg">24時間</span>
                  </td>
                  <td className="px-6 py-4 text-sm text-center text-gray-700">平日8:00-17:00</td>
                  <td className="px-6 py-4 text-sm text-center text-gray-700">平日24時間</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-6 py-4 text-sm font-medium text-gray-900">各種手数料</td>
                  <td className="px-6 py-4 text-sm text-center bg-red-50">
                    <span className="text-red-600 font-bold text-lg">0円</span>
                  </td>
                  <td className="px-6 py-4 text-sm text-center text-gray-700">0円</td>
                  <td className="px-6 py-4 text-sm text-center text-gray-700">0円</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-6 py-4 text-sm font-medium text-gray-900">スマホアプリ</td>
                  <td className="px-6 py-4 text-sm text-center bg-red-50">
                    <span className="text-red-600 font-bold text-lg">◎</span>
                  </td>
                  <td className="px-6 py-4 text-sm text-center text-gray-700">◎</td>
                  <td className="px-6 py-4 text-sm text-center text-gray-700">○</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-6 py-4 text-sm font-medium text-gray-900">サポート評価</td>
                  <td className="px-6 py-4 text-sm text-center bg-red-50">
                    <span className="bg-red-600 text-white px-2 py-1 rounded text-xs font-bold">HDI三つ星</span>
                  </td>
                  <td className="px-6 py-4 text-sm text-center text-gray-700">-</td>
                  <td className="px-6 py-4 text-sm text-center text-gray-700">-</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="mt-8 bg-gradient-to-r from-red-50 to-orange-50 rounded-xl p-6 max-w-4xl mx-auto shadow-md border-2 border-red-200">
            <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center">
              <i className="ri-star-fill text-red-500 mr-2"></i>
              総合評価
            </h3>
            <p className="text-gray-700 leading-relaxed">
              <span className="text-red-600 font-black text-xl">DMM FXなら</span>、
              <strong className="text-red-600 text-lg">業界最狭水準のスプレッド</strong>、
              <strong className="text-red-600 text-lg">使いやすいツール</strong>、
              <strong className="text-red-600 text-lg">24時間サポート</strong>、
              <strong className="text-red-600 text-lg">各種手数料0円</strong>が魅力です。最低取引単位は10,000通貨からですが、レバレッジを活用すれば約6万円から取引可能。初心者から上級者まで幅広くおすすめできるFX業者です。
            </p>
          </div>
        </div>
      </section>

      {/* まとめ */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">まとめ：初心者におすすめのFX業者</h2>
          
          <div className="bg-white rounded-2xl p-8 mb-8 shadow-lg border-l-4 border-red-500">
            <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
              <span className="text-red-600 text-3xl mr-2">DMM FX</span>がおすすめな理由
            </h3>
            <div className="space-y-4">
              <div className="flex items-start bg-red-50 rounded-lg p-4">
                <i className="ri-check-double-line text-red-600 text-2xl mr-3 mt-1"></i>
                <div>
                  <p className="text-gray-900 font-bold">業界最狭水準のスプレッド</p>
                  <p className="text-red-600 font-bold text-lg">米ドル/円0.2銭で取引コストを抑えられる</p>
                </div>
              </div>
              <div className="flex items-start bg-red-50 rounded-lg p-4">
                <i className="ri-check-double-line text-red-600 text-2xl mr-3 mt-1"></i>
                <div>
                  <p className="text-gray-900 font-bold">初心者でも使いやすい取引ツール</p>
                  <p className="text-red-600 font-bold text-lg">DMMFX STANDARD / PLUSを用意</p>
                </div>
              </div>
              <div className="flex items-start bg-red-50 rounded-lg p-4">
                <i className="ri-check-double-line text-red-600 text-2xl mr-3 mt-1"></i>
                <div>
                  <p className="text-gray-900 font-bold">充実のサポート体制</p>
                  <p className="text-red-600 font-bold text-lg">24時間対応（LINE/電話/メール）・HDI格付け三つ星</p>
                </div>
              </div>
              <div className="flex items-start bg-red-50 rounded-lg p-4">
                <i className="ri-check-double-line text-red-600 text-2xl mr-3 mt-1"></i>
                <div>
                  <p className="text-gray-900 font-bold">安心の安全性</p>
                  <p className="text-red-600 font-bold text-lg">金融庁登録・全額信託保全</p>
                </div>
              </div>
              <div className="flex items-start bg-red-50 rounded-lg p-4">
                <i className="ri-check-double-line text-red-600 text-2xl mr-3 mt-1"></i>
                <div>
                  <p className="text-gray-900 font-bold">各種手数料が完全無料</p>
                  <p className="text-red-600 font-bold text-lg">取引手数料・入出金手数料・口座維持費すべて0円</p>
                </div>
              </div>
            </div>
          </div>

          {/* 口座開設手順へのリンク */}
          <div className="mb-8 bg-white rounded-xl p-6 border-l-4 border-emerald-500 shadow-md">
            <p className="text-gray-700 leading-relaxed">
              口座開設の具体的な手順は、こちらで画像付きで解説しています。<br />
              → <Link to="/account-opening" className="text-emerald-600 font-semibold hover:text-emerald-700 underline cursor-pointer">「DMM FXの口座開設手順を画像付きで解説」</Link>
            </p>
          </div>

          <div className="text-center">
            <p className="text-gray-700 mb-6">
              7つのチェックポイントを総合的に評価すると、<br />
              <span className="text-red-600 text-2xl font-black">DMM FXは初心者に最もおすすめのFX業者</span>です。
            </p>
            <Link 
              to="/reviews" 
              className="inline-block text-teal-600 hover:text-teal-700 font-semibold underline cursor-pointer"
            >
              DMM FXの口コミ・評判を見る →
            </Link>
          </div>
        </div>
      </section>

      {/* Related Articles Section */}
      <section className="py-16 bg-gradient-to-br from-slate-50 to-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">関連記事</h2>
            <p className="text-gray-600 text-base">もっと詳しく知りたい方へ</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Link to="/comparison" className="bg-white rounded-xl p-6 border border-gray-200 hover:shadow-lg transition-all cursor-pointer">
              <div className="w-12 h-12 bg-gradient-to-br from-teal-500 to-emerald-600 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-scales-line text-white text-xl"></i>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">他社との徹底比較</h3>
              <p className="text-sm text-gray-600 mb-4">GMOクリック証券との違いを詳しく解説</p>
              <div className="flex items-center text-teal-600 text-sm font-semibold">
                <span>詳しく見る</span>
                <i className="ri-arrow-right-line ml-2"></i>
              </div>
            </Link>

            <Link to="/spread" className="bg-white rounded-xl p-6 border border-gray-200 hover:shadow-lg transition-all cursor-pointer">
              <div className="w-12 h-12 bg-gradient-to-br from-teal-500 to-emerald-600 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-funds-line text-white text-xl"></i>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">スプレッドは本当に狭い？</h3>
              <p className="text-sm text-gray-600 mb-4">取引コストを他社と徹底比較</p>
              <div className="flex items-center text-teal-600 text-sm font-semibold">
                <span>詳しく見る</span>
                <i className="ri-arrow-right-line ml-2"></i>
              </div>
            </Link>

            <Link to="/safety" className="bg-white rounded-xl p-6 border border-gray-200 hover:shadow-lg transition-all cursor-pointer">
              <div className="w-12 h-12 bg-gradient-to-br from-teal-500 to-emerald-600 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-shield-check-line text-white text-xl"></i>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">危険？安全性を解説</h3>
              <p className="text-sm text-gray-600 mb-4">金融庁登録・信託保全など、安全性について詳しく解説</p>
              <div className="flex items-center text-teal-600 text-sm font-semibold">
                <span>詳しく見る</span>
                <i className="ri-arrow-right-line ml-2"></i>
              </div>
            </Link>

            <Link to="/support" className="bg-white rounded-xl p-6 border border-gray-200 hover:shadow-lg transition-all cursor-pointer">
              <div className="w-12 h-12 bg-gradient-to-br from-teal-500 to-emerald-600 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-customer-service-2-line text-white text-xl"></i>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">サポート体制は？</h3>
              <p className="text-sm text-gray-600 mb-4">24時間サポートの詳細を確認</p>
              <div className="flex items-center text-teal-600 text-sm font-semibold">
                <span>詳しく見る</span>
                <i className="ri-arrow-right-line ml-2"></i>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">
            今がチャンス！お得に取引を始めよう
          </h2>
          <p className="text-lg text-gray-600 mb-8">
            最大500,000円のキャッシュバックを受け取って、FX取引をスタート
          </p>
          <a 
            href="https://px.a8.net/svt/ejp?a8mat=4AX8OA+BW2OQA+1WP2+6JC82" 
            rel="nofollow"
            className="inline-block bg-gradient-to-r from-teal-500 to-emerald-600 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:shadow-xl transition-all whitespace-nowrap cursor-pointer"
          >
            無料で口座開設する
          </a>
          <img border="0" width="1" height="1" src="https://www13.a8.net/0.gif?a8mat=4AX8OA+BW2OQA+1WP2+6JC82" alt="" style={{ display: 'none' }} />
        </div>
      </section>

      <Footer />
    </div>
  );
}
